// Widget board — the action layer of home (Ships 1-3, Aug 25-26, 2026).
//
// Renders this user's preloaded widgets (registry.ts role defaults or a
// hand-set per-person override) between the schedule grid and the My Business
// zone. Greg's page order: "set up to get to activity, to work — and then they
// can review stuff if they want." The "today" key is the mobile hero and
// renders separately at the top of HomeRedesign, not here.

import type { ReactNode } from "react";
import ApprovalsWidget from "./ApprovalsWidget";
import ArWidget from "./ArWidget";
import BuildQueueWidget from "./BuildQueueWidget";
import BuildsWeekWidget from "./BuildsWeekWidget";
import CallListWidget from "./CallListWidget";
import CoachingWidget from "./CoachingWidget";
import CompanyFunnelWidget from "./CompanyFunnelWidget";
import FlightsWidget from "./FlightsWidget";
import IinkWidget from "./IinkWidget";
import LeaderboardWidget from "./LeaderboardWidget";
import BuildRevenueWidget from "./BuildRevenueWidget";
import PipelineWidget from "./PipelineWidget";
import PnlWidget from "./PnlWidget";
import PreinspWidget from "./PreinspWidget";
import QueuesWidget from "./QueuesWidget";
import ReferralsWidget from "./ReferralsWidget";
import RoofMasterWidget from "./RoofMasterWidget";
import OpsRequestsWidget from "./OpsRequestsWidget";
import RsMeetingsWidget from "./RsMeetingsWidget";
import MyAreasWidget from "./MyAreasWidget";
import { resolveWidgets, type WidgetKey } from "./registry";

// Desktop grid spans per widget (12-col grid; mobile stacks in order).
const SPAN: Record<WidgetKey, string> = {
  today: "",
  pipeline: "lg:col-span-7",
  call_list: "lg:col-span-5",
  approvals: "lg:col-span-6",
  company_funnel: "lg:col-span-6",
  ar: "lg:col-span-7",
  queues: "lg:col-span-5",
  iink: "lg:col-span-5",
  flights: "lg:col-span-7",
  rs_meetings: "lg:col-span-5",
  preinsp: "lg:col-span-5",
  build_queue: "lg:col-span-5",
  coaching: "lg:col-span-7",
  referrals: "lg:col-span-6",
  builds_week: "lg:col-span-7",
  roof_master: "lg:col-span-7",
  ops_requests: "lg:col-span-12",
  pnl: "lg:col-span-12",
  leaderboard: "lg:col-span-6",
  company_revenue: "lg:col-span-12",
  my_areas: "lg:col-span-12",
};

export default function WidgetBoard({
  memberId,
  role,
  email,
  scopeIds,
  scopeNote,
  customKeys,
  title = "Your board",
}: {
  memberId: string;
  role: string | null | undefined;
  /** Drives the registry's `excludeEmails` carve-out (financials). */
  email?: string | null;
  scopeIds: string[] | null;
  scopeNote?: string | null;
  /**
   * Saved board from `user_widget_layout` (set on /team-boards). Beats the
   * registry defaults; null/empty falls back to them. Still re-filtered
   * through the role gate by resolveWidgets.
   */
  customKeys?: WidgetKey[] | null;
  /** Header label — /team-boards renders this board as a preview. */
  title?: string | null;
}) {
  const keys = resolveWidgets(customKeys, memberId, role, email).filter((k) => k !== "today");
  if (keys.length === 0) return null;

  const render = (k: WidgetKey): ReactNode => {
    switch (k) {
      case "pipeline":
        return <PipelineWidget scopeIds={scopeIds} scopeNote={scopeNote} />;
      case "call_list":
        return <CallListWidget />;
      case "approvals":
        return <ApprovalsWidget />;
      case "company_funnel":
        return (
          <CompanyFunnelWidget />
        );
      case "ar":
        return <ArWidget />;
      case "queues":
        return <QueuesWidget />;
      case "iink":
        return <IinkWidget />;
      case "flights":
        return <FlightsWidget memberId={memberId} />;
      case "rs_meetings":
        return <RsMeetingsWidget memberId={memberId} />;
      case "preinsp":
        // Robert assigns climbs, so his board reads company-wide; an RS sees
        // only their own.
        return (
          <PreinspWidget
            memberId={memberId}
            scope={role === "roof_team_manager" ? "company" : "mine"}
          />
        );
      case "build_queue":
        return <BuildQueueWidget />;
      case "coaching":
        return <CoachingWidget memberId={memberId} />;
      case "referrals":
        return <ReferralsWidget />;
      case "builds_week":
        return <BuildsWeekWidget />;
      case "roof_master":
        return <RoofMasterWidget />;
      case "ops_requests":
        return <OpsRequestsWidget memberId={memberId} />;
      case "pnl":
        return <PnlWidget />;
      case "leaderboard":
        return <LeaderboardWidget />;
      case "company_revenue":
        return <BuildRevenueWidget memberId={memberId} role={role} />;
      case "my_areas":
        return <MyAreasWidget memberId={memberId} />;
      default:
        return null;
    }
  };

  return (
    <div className="mt-6">
      {title && (
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">
          {title}
        </div>
      )}
      {/* flex gap (not space-y) + empty:hidden so a widget that renders
          nothing — "my_areas" for someone with no area — leaves no gap. */}
      <div className="flex flex-col gap-4 lg:grid lg:grid-cols-12 lg:gap-6 lg:items-start">
        {keys.map((k) => (
          <div key={k} className={`${SPAN[k]} empty:hidden`}>
            {render(k)}
          </div>
        ))}
      </div>
    </div>
  );
}
